import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  DialogContentText,
  ThemeProvider,
  createTheme,
  useTheme,
  useMediaQuery,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { COLORS } from "colors/color";
import {
  Checklist,
  Language,
  WorkspacePremium,
  SmartToy as SmartToyIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  InfoOutlined as InfoOutlinedIcon,
  LinkedIn as LinkedInIcon,
  Download,
  Work as WorkIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  WorkHistoryOutlined,
  Close,
  Delete,
  Book as BookIcon,
} from "@mui/icons-material";
import { GraduationCap, MapPin } from "lucide-react";
import { styled } from "@mui/material/styles";
import SendEmailModal from "../Emailing/SendEmailModal";
import EmailHistory from "../Emailing/EmailHistory";
import axios from "axios";
import ApplicationSearchBar from "./ApplicationSearchBar";
import { OutlinedButton } from "../../components/Buttons/Buttons";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import jsPDF from "jspdf";
import "jspdf-autotable";
const apiUrl = process.env.REACT_APP_API_URL;

// Default theme to ensure components have access to theme even if parent doesn't provide one
const defaultTheme = createTheme();

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: "24px",
  marginBottom: theme.spacing(4),
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  border: "1px solid rgba(0, 23, 231, 0.06)",
}));

const InfoChipContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.5),
  },
}));

const ResponsiveAvatar = styled(Avatar)(({ theme }) => ({
  width: 90,
  height: 90,
  marginRight: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: 60,
    height: 60,
    marginRight: theme.spacing(1),
  },
  backgroundColor: "rgba(0, 23, 231, 0.08)",
  backdropFilter: "blur(4px)",
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
  borderRadius: "12px",
  backgroundColor: "rgba(0, 23, 231, 0.08)",
  color: "#0017E7",
  border: "1px solid rgba(0, 23, 231, 0.15)",
  "& .MuiChip-label": {
    padding: "8px 14px",
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  "& .MuiChip-icon": {
    color: "#0017E7",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "10px",
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  color: "#0017E7",
  backgroundColor: "transparent",
  border: "1px solid rgba(0, 23, 231, 0.2)",
}));

const StyledTypography = styled(Typography)(() => ({
  fontFamily: "Poppins",
  color: "#4a5568",
}));

// Define a common style for all Show More buttons
const showMoreInlineSx = {
  display: "inline",
  fontSize: "1em",
  minWidth: 0,
  padding: 0,
  height: "auto",
  ml: 0.5,
  mb: 0,
  color: "primary.main",
  textTransform: "none",
  background: "none",
  fontWeight: 400,
  verticalAlign: "baseline",
  lineHeight: "inherit",
  letterSpacing: "inherit",
  border: "none",
};

// Updated helper functions
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch (error) {
    console.error("Failed to parse JWT:", error);
    return {};
  }
};

// Improved function to parse string arrays and clean them properly
const parseStringToArray = (data) => {
  if (!data) return [];

  // If it's already an array, just return it
  if (Array.isArray(data)) return data;

  // If it's a string, try to clean and parse it
  if (typeof data === "string") {
    // First, check if it's a JSON string
    if (data.startsWith("[") && data.endsWith("]")) {
      try {
        // Try standard JSON parsing first
        return JSON.parse(data);
      } catch (e) {
        // If that fails, try to clean up the format and parse again
        const cleanedString = data
          .replace(/\[|\]|'|"/g, "") // Remove brackets, single and double quotes
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean); // Remove empty strings

        return cleanedString;
      }
    }

    // If it's not a JSON-like string, just split by commas
    if (data.includes(",")) {
      return data
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    // If it's just a single value, return as an array with one item
    return [data];
  }

  // If we can't parse it, return an empty array
  return [];
};

// Clean a string by removing quotes and brackets
const cleanString = (str) => {
  if (!str) return "";
  return str.replace(/^\[|\]$|'|"/g, "").trim();
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString; // Return the original string if parsing fails
  }
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1500,
    },
  },
});

const ShowMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: "0.875rem",
  textTransform: "none",
  color: "#0017E7",
  backgroundColor: "transparent",
  border: "1px solid rgba(0, 23, 231, 0.2)",
  borderRadius: "10px",
  padding: "6px 12px",
  fontWeight: 500,
}));

const truncateLabel = (label, maxLength = 5) => {
  if (typeof label !== "string") return label;
  // Get window width
  const windowWidth = window.innerWidth;
  // Always use 5 as the max length for show more
  let adjustedMaxLength = maxLength;
  return label.length > adjustedMaxLength
    ? label.slice(0, adjustedMaxLength) + "..."
    : label;
};

// Special function for location chips with longer character limit (5-15 characters)
const truncateLocationLabel = (label, maxLength = 15) => {
  if (typeof label !== "string") return label;
  return label.length > maxLength
    ? label.slice(0, maxLength) + "..."
    : label;
};

// Add new function for truncating experience text
const truncateExperienceText = (text) => {
  if (typeof text !== "string") return text;

  const windowWidth = window.innerWidth;
  let maxLength;

  if (windowWidth <= 1920) {
    // 1080p
    maxLength = 9;
  } else if (windowWidth <= 2560) {
    // 1440p
    maxLength = 11;
  } else {
    maxLength = 13;
  }

  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

const ViewToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
  color: "#ffffff",
  border: "none",
}));

const ApplicationTable = ({
  candidates: initialCandidates,
  post_title,
  viewType,
  setViewType,
  dataType = "regular",
  onDelete,
  hideSearchBar = false,
  hideSwitchButton = false,
  hideCoverLetter = false, // Add hideCoverLetter prop
  onNavigateToBlacklist = null, // Navigation callback for blacklist
  onNavigateToDynamicForms = null, // Navigation callback for dynamic forms
  location = "", // Add location prop with default empty string
  initialTotalCount = null, // Total count from infinite scroll
  enableInfiniteScroll = false, // Whether infinite scroll is enabled
  infiniteScrollPageSize = 20, // Page size for infinite scroll
  fetchMoreData = null, // Function to fetch more data for infinite scroll
  onSearchResults = null, // Function to handle search results for infinite scroll
  infiniteScrollData = null, // Data from infinite scroll component
  infiniteScrollTotalCount = null, // Total count from infinite scroll component
  showSourceInfo = false, // Whether to show source information (for DataImportViewers)
}) => {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [scoreDetailsOpen, setScoreDetailsOpen] = useState(false);
  const [selectedScoreDetails, setSelectedScoreDetails] = useState(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isEmailHistoryOpen, setIsEmailHistoryOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [moreItemsDialogOpen, setMoreItemsDialogOpen] = useState(false);
  const [moreItemsDialogTitle, setMoreItemsDialogTitle] = useState("");
  const [moreItemsDialogContent, setMoreItemsDialogContent] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [scale, setScale] = useState(1);
  const [currentSearchParams, setCurrentSearchParams] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Enhanced deduplication function to handle same ID from different sources
  const deduplicateCandidates = useCallback((candidates) => {
    if (!Array.isArray(candidates)) return [];
    
    const seen = new Map(); // Use Map to store composite ID combinations
    const deduplicated = [];
    
    for (const candidate of candidates) {
      if (!candidate || !candidate.id) continue;
      
      // Create composite unique key combining ID and source
      // This ensures different people with same ID from different models are kept separate
      const compositeId = `${candidate.source || 'unknown'}_${candidate.id}`;
      
      if (!seen.has(compositeId)) {
        seen.set(compositeId, true);
        
        // Add composite ID to candidate for React key generation
        const candidateWithCompositeId = {
          ...candidate,
          compositeId: compositeId
        };
        
        deduplicated.push(candidateWithCompositeId);
      } else {
        console.warn(`🚨 TRUE DUPLICATE REMOVED - Composite ID: ${compositeId}, Name: ${candidate.name || candidate.first_name}`);
      }
    }
    
    // Log deduplication summary
    const duplicateCount = candidates.length - deduplicated.length;
    if (duplicateCount > 0) {
      console.log(`🔍 DEDUPLICATION SUMMARY: Removed ${duplicateCount} true duplicate(s) from ${candidates.length} total candidates`);
    } else {
      console.log(`✅ NO DUPLICATES FOUND: All ${candidates.length} candidates are unique`);
    }
    
    return deduplicated;
  }, []);

  // Add scaling effect
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800; // Approximate height of your content
      const contentWidth = 1200; // Approximate width of your content

      // Calculate scale based on both height and width
      const heightScale = (windowHeight * 0.95) / contentHeight; // 95% of window height
      const widthScale = (windowWidth * 0.95) / contentWidth; // 95% of window width

      // Use the smaller scale to ensure content fits both dimensions
      let newScale = Math.min(heightScale, widthScale, 1);

      // Add minimum scale limit based on screen width
      if (windowWidth <= 1920) {
        // For 1080p and similar resolutions
        newScale = Math.max(newScale, 0.8); // Minimum 80% scale
      } else if (windowWidth <= 2560) {
        // For 1440p
        newScale = Math.max(newScale, 0.85); // Minimum 85% scale
      } else {
        newScale = Math.max(newScale, 0.9); // Minimum 90% scale for larger screens
      }

      setScale(newScale);
    };

    handleResize(); // Initial scale
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set initial candidates when component mounts or initialCandidates changes
  useEffect(() => {
    console.log("🔍 INITIAL CANDIDATES DEBUG - initialCandidates:", initialCandidates);
    console.log("🔍 INITIAL CANDIDATES DEBUG - initialCandidates type:", typeof initialCandidates);
    console.log("🔍 INITIAL CANDIDATES DEBUG - isArray:", Array.isArray(initialCandidates));
    console.log("🔍 INITIAL CANDIDATES DEBUG - enableInfiniteScroll:", enableInfiniteScroll);

    // If infinite scroll is enabled, don't manage local state - let the infinite scroll component handle it
    if (enableInfiniteScroll) {
      console.log("🔍 INITIAL CANDIDATES DEBUG - Infinite scroll enabled, skipping local state management");
      return;
    }

    if (Array.isArray(initialCandidates)) {
      // Map source to dataType for unified candidates (from backend)
      const normalizedInitialCandidates = initialCandidates.map(candidate => {
        if (candidate.source && !candidate.dataType) {
          console.log("🔍 MAPPING DEBUG - Mapping source to dataType:", candidate.source, "for candidate:", candidate.id);
          return {
            ...candidate,
            dataType: candidate.source // Map source to dataType for compatibility
          };
        }
        return candidate;
      });

      console.log("🔍 MAPPING DEBUG - After mapping, normalizedInitialCandidates:", normalizedInitialCandidates.slice(0, 2));
      console.log("🔍 SET CANDIDATES DEBUG - Setting candidates with dataType mapping:", normalizedInitialCandidates.slice(0, 2).map(c => ({ id: c.id, source: c.source, dataType: c.dataType })));
      setCandidates(normalizedInitialCandidates);
      setTotalCount(normalizedInitialCandidates.length);
    } else if (
      initialCandidates &&
      typeof initialCandidates === "object" &&
      initialCandidates.results
    ) {
      // Handle paginated response format from backend
      // Map source to dataType for unified candidates (from backend)
      const normalizedResults = initialCandidates.results.map(candidate => {
        if (candidate.source && !candidate.dataType) {
          console.log("🔍 MAPPING DEBUG - Mapping source to dataType:", candidate.source, "for candidate:", candidate.id);
          return {
            ...candidate,
            dataType: candidate.source // Map source to dataType for compatibility
          };
        }
        return candidate;
      });

      console.log("🔍 MAPPING DEBUG - After mapping, normalizedResults:", normalizedResults.slice(0, 2));
      setCandidates(normalizedResults);
      setTotalCount(initialCandidates.count || normalizedResults.length);
      console.log(
        `ApplicationTable: Loaded ${normalizedResults.length} candidates from paginated response. Total: ${initialCandidates.count}`
      );
    } else {
      console.error(
        "Initial candidates prop is not an array:",
        initialCandidates
      );
      setCandidates([]);
      setTotalCount(0);
    }
  }, [initialCandidates, enableInfiniteScroll, initialTotalCount]);

  // Debug: Log when candidates state changes
  useEffect(() => {
    console.log("🔍 CANDIDATES STATE DEBUG - candidates state changed:", candidates.slice(0, 2).map(c => ({ id: c.id, source: c.source, dataType: c.dataType })));
  }, [candidates]);

  // Update the search API endpoints based on dataType
  const SEARCH_API_ENDPOINT = `${apiUrl}candidates/api/${
    dataType === "regular"
      ? "applications/search/"
      : dataType === "scraped"
      ? "applications-scraped/search/"
      : "scraped-users/search/"
  }`;

  const AUTOCOMPLETE_API_ENDPOINT = `${apiUrl}candidates/api/${
    dataType === "regular"
      ? "applications/autocomplete/"
      : dataType === "scraped"
      ? "applications-scraped/autocomplete/"
      : "scraped-users/autocomplete/"
  }`;

  // Search result handler
  const handleSearchResults = (results, totalCount = null, searchParams = {}) => {
    console.log("🔍 SEARCH DEBUG - handleSearchResults called with:", {
      results: results,
      totalCount: totalCount,
      searchParams: searchParams,
      enableInfiniteScroll: enableInfiniteScroll,
      onSearchResults: !!onSearchResults
    });

    // If infinite scroll is enabled and onSearchResults is provided, delegate to it
    if (enableInfiniteScroll && onSearchResults) {
      console.log("🔍 SEARCH DEBUG - Delegating to infinite scroll handler");
      onSearchResults(results, totalCount, searchParams);
      return;
    } else {
      console.log("🔍 SEARCH DEBUG - NOT delegating to infinite scroll:", {
        enableInfiniteScroll: enableInfiniteScroll,
        onSearchResults: !!onSearchResults,
        willUpdateLocalState: true
      });
    }

    // Store the current search parameters for infinite scroll
    setCurrentSearchParams(searchParams);

    // Handle null results (clear button clicked)
    if (results === null) {
      console.log("🔍 SEARCH DEBUG - Clear button clicked, resetting...");
      if (enableInfiniteScroll && onSearchResults) {
        // For infinite scroll, delegate the reset to the parent
        onSearchResults(null, 0, {});
      } else {
        // For regular mode, reset local state
        setCandidates(initialCandidates);
        setTotalCount(initialCandidates?.length || 0);
        setCurrentSearchParams({}); // Clear search params
      }
      return;
    }

    if (Array.isArray(results)) {
      // Map source to dataType for unified candidates (from backend)
      const normalizedResults = results.map(candidate => {
        if (candidate.source && !candidate.dataType) {
          return {
            ...candidate,
            dataType: candidate.source // Map source to dataType for compatibility
          };
        }
        return candidate;
      });

      // For infinite scroll mode, don't update local state directly
      // The parent infinite scroll component will handle the data updates
      if (!enableInfiniteScroll) {
        setCandidates(normalizedResults);
        const newTotalCount = totalCount !== null ? totalCount : normalizedResults.length;
        setTotalCount(newTotalCount);
      }
    } else {
      console.error("Search results are not an array:", results);
      if (!enableInfiniteScroll) {
        setCandidates([]);
        setTotalCount(0);
      }
    }
  };

  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;
    try {
      return parseJwt(accessToken).user_id;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  };

  const handleEmailClick = (candidateRaw) => {
    console.log("🔍 handleEmailClick called with candidateRaw:", candidateRaw);
    const candidate = getCandidateData(candidateRaw);
    console.log("🔍 Processed candidate data:", candidate);
    console.log("🔍 candidate.first_name:", candidate.first_name);
    console.log("🔍 candidate.last_name:", candidate.last_name);
    console.log("🔍 candidate.email:", candidate.email);
    
    setSelectedCandidate({
      ...candidateRaw,
      ...candidate,
      email: candidate.email
    });
    setIsTemplateModalOpen(true);
  };

  const handleEmailHistoryClick = (candidateRaw) => {
    const candidate = getCandidateData(candidateRaw);
    setSelectedCandidate({
      ...candidateRaw,
      ...candidate,
      email: candidate.email
    });
    setIsEmailHistoryOpen(true);
  };

  const handleResumeClick = async (resumePath) => {
    console.log("resumePath22", resumePath);
    if (!resumePath) {
      alert(t("applicationTable.resumeNotAvailable", "Resume not available"));
      return;
    }

    try {
      

      const normalizedPath = resumePath.replace(/\\/g, "/");
      let fullPath;

          if (dataType === "scraped_users") {
      // For scraped users, cv_file_path is relative to media folder
      fullPath = normalizedPath.startsWith(apiUrl)
        ? normalizedPath
        : `${apiUrl}media/${normalizedPath}`;
    } else if (dataType === "scraped") {
        // For scraped applications
        fullPath = normalizedPath.startsWith(apiUrl)
          ? normalizedPath
          : `${apiUrl}media/${normalizedPath}`;
      } else {
        // For regular applications
        fullPath = normalizedPath.startsWith(apiUrl)
          ? normalizedPath
          : `${apiUrl}media/${normalizedPath}`;
      }

      

      const response = await axios.post(
        `${apiUrl}ai/search_resume/`,
        { resume_url: fullPath },
        { responseType: "blob" }
      );

      if (response.headers["content-type"].includes("application/pdf")) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        console.error(
          "Unexpected response type:",
          response.headers["content-type"]
        );
      }
    } catch (error) {
      console.error("Resume open error:", error);
      console.error("Error details:", error.response || error.message); // More detailed error logging
      alert(
        t(
          "applicationTable.failedToOpenResume",
          "Failed to open resume. Please try again later."
        )
      );
    }
  };

  const handleCoverLetterClick = async (coverLetter) => {
    if (!coverLetter) {
      alert(
        t(
          "applicationTable.coverLetterNotAvailable",
          "Cover letter not available"
        )
      );
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}ai/search_resume/`,
        { resume_url: coverLetter },
        { responseType: "blob" }
      );

      if (response.headers["content-type"].includes("application/pdf")) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        console.error(
          "Unexpected response type:",
          response.headers["content-type"]
        );
      }
    } catch (error) {
      console.error("Cover letter open error:", error);
      alert(
        t(
          "applicationTable.failedToOpenCoverLetter",
          "Failed to open cover letter. Please try again later."
        )
      );
    }
  };

  const handleScoreDetailsClick = (candidate) => {
    setSelectedScoreDetails(candidate.detail_score || {});
    setScoreDetailsOpen(true);
  };

  const handleShowMore = (title, items) => {
    console.log('=== HANDLESHOWMORE CALLED ===');
    console.log('Title:', title);
    console.log('Items received:', items);
    console.log('Items type:', typeof items);
    console.log('Items length:', items?.length);
    
    setMoreItemsDialogTitle(title);
    
    const processedContent = items.map((item, index) => {
      console.log(`Processing item ${index}:`, item);
      console.log(`Item ${index} type:`, typeof item);
      
      // Handle experience objects with detailed information
      if (
        title === "Experience" &&
        typeof item === "object" &&
        item !== null
      ) {
        const result = {
          type: "experience",
          position: item.title || item.position || item.text || "Unknown position",
          company: item.subtitle || item.company || "",
          date_range: item.date_range || "",
          responsibilities: item.description ? [item.description] : (item.responsibilities || []),
        };
        console.log(`Experience item ${index} processed:`, result);
        return result;
      }
      // Handle education objects with detailed information
      if (
        title === "Education" &&
        typeof item === "object" &&
        item !== null
      ) {
        const result = {
          type: "education",
          text: item.title || item.text || "",
          subtitle: item.subtitle || "",
          date_range: item.date_range || "",
          description: item.description || "",
        };
        console.log(`Education item ${index} processed:`, result);
        return result;
      }
      // Handle LinkedIn links
      if (typeof item === "string" && item.includes("LinkedIn:")) {
        const linkedInUrl = item.replace("LinkedIn:", "").trim();
        const result = {
          type: "link",
          label: t("applicationTable.seeLinkedInProfile", "LinkedIn Profile"),
          href: linkedInUrl.startsWith("http")
            ? linkedInUrl
            : `https://www.linkedin.com/in/${linkedInUrl}`,
        };
        console.log(`LinkedIn string item ${index} processed:`, result);
        return result;
      }
      // Handle any other objects - convert to string safely
      if (typeof item === "object" && item !== null) {
        // Check if it's already a link object
        if (item.type === "link") {
          console.log(`Link object item ${index} passed through:`, item);
          return item;
        }
        // Try to extract meaningful text from object
        const text =
          item.position ||
          item.text ||
          item.label ||
          item.name ||
          JSON.stringify(item);
        console.log(`Other object item ${index} converted to string:`, text);
        return String(text);
      }
      // Ensure all items are strings
      console.log(`String item ${index} passed through:`, String(item));
      return String(item);
    });
    
    console.log('=== FINAL PROCESSED CONTENT ===');
    console.log('Processed content:', processedContent);
    processedContent.forEach((item, idx) => {
      console.log(`processedContent[${idx}]:`, item);
      console.log(`  - type: ${typeof item}`);
      if (typeof item === 'object' && item !== null) {
        console.log(`  - object type property: ${item.type}`);
      }
    });
    
    setMoreItemsDialogContent(processedContent);
    setMoreItemsDialogOpen(true);
  };

  // Function to render limited items with "Show More" button
  const renderLimitedItems = (items, title, limit = 1, charLimit = 30) => {
    if (!items || items.length === 0) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            fontSize: "0.95rem",
            fontStyle: "italic",
            color: "rgba(0, 23, 231, 0.6)",
          }}
        >
          {t("applicationTable.noItemsListed", "No {{title}} listed", {
            title: title.toLowerCase(),
          })}
        </Typography>
      );
    }

    // Helper function to check if any item needs truncation
    const needsTruncation = (item) => {
      // Safety check for objects
      let displayItem = item;
      if (typeof item === "object" && item !== null) {
        displayItem = item.position || item.text || JSON.stringify(item);
      }
      return cleanString(displayItem).length > charLimit;
    };
    const hasLongContent = items.some(needsTruncation);

    return (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: { xs: "column", xl: "row" },
            alignItems: { xs: "flex-start", xl: "center" },
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {items.slice(0, limit).map((item, i) => {
              // Safety check: if item is an object, convert it to string
              let displayItem = item;
              if (typeof item === "object" && item !== null) {
                // Handle new format: {title, subtitle, date_range, description}
                // Handle old format: {position, text, company}
                if (item.title || item.position || item.text) {
                  displayItem = item.title || item.position || item.text || "Unknown";
                } else {
                  displayItem = JSON.stringify(item);
                }
              }

              const cleanedItem = cleanString(displayItem);
              const truncatedItem =
                cleanedItem.length > charLimit
                  ? `${cleanedItem.substring(0, charLimit)}...`
                  : cleanedItem;

              return (
                <Chip
                  key={i}
                  label={truncatedItem}
                  sx={{
                    height: "32px",
                    backgroundColor: "rgba(0, 23, 231, 0.08)",
                    color: "#0017E7",
                    border: "1px solid rgba(0, 23, 231, 0.15)",
                    fontWeight: 500,

                    "& .MuiChip-label": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "200px",
                      fontSize: "0.875rem",
                      lineHeight: "1.4",
                      fontWeight: "500",
                    },
                  }}
                />
              );
            })}
          </Box>

          {(items.length > limit || hasLongContent) && (
            <ShowMoreButton
              onClick={() => handleShowMore(title, items)}
              size="small"
              sx={{
                p: "6px 12px",
                minHeight: 0,
                lineHeight: "normal",
                textTransform: "none",
                fontSize: "0.8rem",
                minWidth: "auto",
                ml: 1,
                borderRadius: "8px",
                color: "#0017E7",
                backgroundColor: "rgba(0, 23, 231, 0.08)",
                border: "1px solid rgba(0, 23, 231, 0.15)",
                fontWeight: 500,
              }}
            >
              {items.length > limit
                ? t("applicationTable.showMore", "Show {{count}} more", {
                    count: items.length - limit,
                  })
                : t("applicationTable.showFullContent", "Show full content")}
            </ShowMoreButton>
          )}
        </Box>
      </>
    );
  };



  // Function to process regular application data
  const processRegularApplicationData = (candidate) => {
    // Add null check to prevent errors
    if (!candidate) {
      console.warn('processRegularApplicationData: candidate is null or undefined');
      return {
        name: t("applicationTable.noName", "No Name"),
        firstName: "",
        lastName: "",
        email: t("applicationTable.noEmail", "No Email"),
        phone: t("applicationTable.noPhone", "No Phone"),
        profile: t("applicationTable.noProfileAvailable", "No profile available"),
        location: t("applicationTable.noLocationProvided", "No location provided"),
        image: null,
        skills: [],
        education: [],
        languages: [],
        certificates: [],
        experiences: [],
        linkedIn: "",
        resume: "",
        resume_path: "",
        score: 0,
        date: new Date().toISOString(),
        detailScore: {},
        extracted_informations: {}
      };
    }

    const candidateData = candidate.candidate || candidate;

    // Use extracted_informations as single source of truth
    const extractedInfo = candidate.extracted_informations || {};

    // Get all data from extracted_informations with fallbacks to individual fields only if needed
    const name = extractedInfo.Name || 
      `${candidateData.first_name || ""} ${candidateData.last_name || ""}`.trim() ||
      t("applicationTable.noName", "No Name");
      
    const email = extractedInfo.Email || 
      candidateData.email || 
      candidate.email ||
      t("applicationTable.noEmail", "No Email");
      
    const phone = extractedInfo.Phone || 
      candidateData.phone || 
      t("applicationTable.noPhone", "No Phone");
      
    const profile = extractedInfo.Profil || 
      candidateData.profile ||
      candidateData.biography ||
      t("applicationTable.noProfileAvailable", "No profile available");
      
    // Handle Address field - it can be an array or string
    let locationFromAddress = extractedInfo.Address || "";
    if (Array.isArray(locationFromAddress)) {
      locationFromAddress = locationFromAddress.join(", ");
    }
    
    const location = locationFromAddress || 
      [candidateData.city, candidateData.country].filter(Boolean).join(", ") ||
      candidateData.address ||
      t("applicationTable.noLocationProvided", "No location provided");

    // Get image (not typically in extracted_informations)
    let image = candidateData.image || candidate.image || null;
    if (image && !image.startsWith("http")) {
      image = `${apiUrl}media/${image.replace(/^\\|\//, "")}`;
    }

    // Get structured data from extracted_informations and ensure arrays
    const skills = extractedInfo.Skills || [];
    const education = extractedInfo.Education || [];
    const languages = extractedInfo.Language || [];
    const certificates = extractedInfo.Certificates || [];
    const experiences = extractedInfo.EXPERIENCE || [];

    // For regular applications, category data is not typically available
    let categoryData = null;

    // Ensure all fields are arrays for consistent frontend handling
    const safeSkills = Array.isArray(skills) ? skills : [];
    const safeEducation = Array.isArray(education) ? education : [];
    const safeLanguages = Array.isArray(languages) ? languages : [];
    const safeCertificates = Array.isArray(certificates) ? certificates : [];
    const safeExperiences = Array.isArray(experiences) ? experiences : [];

    return {
      // Basic info - all from extracted_informations
      name: name,
      firstName: name.split(" ")[0] || "",
      lastName: name.split(" ").slice(1).join(" ") || "",
      // Also try to get from individual fields if available
      first_name: name.split(" ")[0] || candidateData.first_name || "",
      last_name: name.split(" ").slice(1).join(" ") || candidateData.last_name || "",
      email: email,
      phone: phone,
      profile: cleanString(profile),
      location: location,
      address: extractedInfo.Address || candidateData.address || "",
      city: candidateData.city || "", // Keep for compatibility
      country: candidateData.country || "", // Keep for compatibility
      postalCode: candidateData.postal_code,
      image,

      // Use only structured data from extracted_informations
      skills: safeSkills,
      education: safeEducation,
      languages: safeLanguages,
      certificates: safeCertificates,
      experiences: safeExperiences,
      
      // Category data (typically null for regular applications)
      category: categoryData,

      // Social info
      linkedIn: extractedInfo.LinkedIn || candidateData.linkedin_profile || "",

      // Resume info
      resume: candidate.resume || candidateData.cv,
      resume_path: candidate.resume_path || "",

      // Additional info
      fieldOfStudy: candidateData.field_of_study || "",
      lastPostOccupied: candidateData.last_post_occupied || "",

      // Application info
      score: candidate.score_matching || 0,
      date:
        candidate.applied_date || candidate.date || new Date().toISOString(),
      detailScore: candidate.detail_score || {},

      // Extracted information - centralized single source of truth
      extracted_informations: {
        ...extractedInfo,
        Skills: safeSkills,
        Education: safeEducation,
        Language: safeLanguages,
        Certificates: safeCertificates,
        EXPERIENCE: safeExperiences,
        agexperiece: extractedInfo.agexperiece || 0,
      },

      // Preserve the dataType field for mixed data handling
      dataType: candidate.dataType,
    };
  };

  // Function to process scraped data
  const processScrapedData = (candidate) => {
    // Add null check to prevent errors
    console.log("candidate44", candidate);
    if (!candidate) {
      console.warn('processScrapedData: candidate is null or undefined');
      return {
        name: t("applicationTable.noName", "No Name"),
        firstName: "",
        lastName: "",
        email: t("applicationTable.noEmail", "No Email"),
        phone: t("applicationTable.noPhone", "No Phone"),
        profile: t("applicationTable.noProfileAvailable", "No profile available"),
        location: t("applicationTable.noLocationProvided", "No location provided"),
        image: null,
        skills: [],
        education: [],
        languages: [],
        certificates: [],
        experiences: [],
        linkedIn: "",
        resume: "",
        resume_path: "",
        score: 0,
        date: new Date().toISOString(),
        detailScore: {},
        extracted_informations: {}
      };
    }

    // Use extracted_informations as single source of truth
    const extractedInfo = candidate.extracted_informations || {};

    // Get all data from extracted_informations with fallbacks to individual fields only if needed
    const name = extractedInfo.Name || 
      candidate.name ||
      t("applicationTable.noName", "No Name");
      
    const email = extractedInfo.Email || 
      candidate.email ||
      t("applicationTable.noEmail", "No Email");
      
    const phone = extractedInfo.Phone || 
      candidate.phone ||
      t("applicationTable.noPhone", "No Phone");
      
    const profile = extractedInfo.Profil || 
      candidate.profil ||
      t("applicationTable.noProfileAvailable", "No profile available");
      
    // Handle Address field - it can be an array or string
    let locationFromAddress = extractedInfo.Address || "";
    if (Array.isArray(locationFromAddress)) {
      locationFromAddress = locationFromAddress.join(", ");
    }
    
    const location = locationFromAddress || 
      candidate.location ||
      t("applicationTable.noLocationProvided", "No location provided");

    // Get structured data from extracted_informations and ensure arrays
    const skills = extractedInfo.Skills || [];
    const education = extractedInfo.Education || [];
    const languages = extractedInfo.Language || [];
    const certificates = extractedInfo.Certificates || [];
    const experiences = extractedInfo.EXPERIENCE || [];
    const yearsOfExperience = extractedInfo.agexperiece || 0;

    // For scraped users, get category information instead of certificates
    let categoryData = null;
    if (candidate.category) {
      console.log("🔍 processScrapedData DEBUG - Found category data:", candidate.category);
      categoryData = {
        id: candidate.category.id,
        name: candidate.category.name
      };
    } else {
      console.log("🔍 processScrapedData DEBUG - No category data found for candidate:", candidate.id);
    }

    // Ensure all fields are arrays for consistent frontend handling
    const safeSkills = Array.isArray(skills) ? skills : [];
    const safeEducation = Array.isArray(education) ? education : [];
    const safeLanguages = Array.isArray(languages) ? languages : [];
    const safeCertificates = Array.isArray(certificates) ? certificates : [];
    const safeExperiences = Array.isArray(experiences) ? experiences : [];

    // Handle resume path
    let resumePath = "";
    if (dataType === "scraped_users") {
      resumePath = candidate.cv_file_path || "";
    } else if (candidate.resume) {
      if (candidate.resume.startsWith("http")) {
        resumePath = candidate.resume;
      } else {
        const normalizedPath = candidate.resume.replace(/\\/g, "/");
        resumePath = normalizedPath.startsWith(apiUrl)
          ? normalizedPath
          : `${apiUrl}media/${normalizedPath}`;
      }
    }

    return {
      // Basic info - all from extracted_informations
      name: name,
      firstName: name.split(" ")[0] || "",
      lastName: name.split(" ").slice(1).join(" ") || "",
      // Also try to get from individual fields if available
      first_name: name.split(" ")[0] || candidate.first_name || "",
      last_name: name.split(" ").slice(1).join(" ") || candidate.last_name || "",
      email: email,
      phone: phone,
      profile: profile,
      location: location,
      image: candidate.image || null,

      // Use only structured data from extracted_informations
      skills: safeSkills,
      education: safeEducation,
      languages: safeLanguages,
      certificates: safeCertificates,
      experiences: safeExperiences,
      
      // Category data for scraped users
      category: categoryData,

      // Social info
      linkedIn: extractedInfo.LinkedIn || candidate.linked_in || "",

      // Resume info
      resume: candidate.resume_path || candidate.cv_file_path || "",
      resume_path: resumePath,

      // Application info
      score: candidate.score_matching || 0,
      date: candidate.date || candidate.upload_date || new Date().toISOString(),
      detailScore: candidate.detail_score || {},

      // Extracted information - centralized single source of truth
      extracted_informations: {
        ...extractedInfo,
        EXPERIENCE: safeExperiences,
        Education: safeEducation,
        Language: safeLanguages,
        Certificates: safeCertificates,
        Skills: safeSkills,
        agexperiece: yearsOfExperience,
      },

      // Preserve the dataType field for mixed data handling
      dataType: candidate.dataType,
    };
  };


  // Function to process mixed data (all candidate types combined)
  const processMixedData = (candidate) => {
    // Add null check to prevent errors
    if (!candidate) {
      console.warn('processMixedData: candidate is null or undefined');
      return {
        name: t("applicationTable.noName", "No Name"),
        firstName: "",
        lastName: "",
        email: t("applicationTable.noEmail", "No Email"),
        phone: t("applicationTable.noPhone", "No Phone"),
        profile: t("applicationTable.noProfileAvailable", "No profile available"),
        location: t("applicationTable.noLocationProvided", "No location provided"),
        image: null,
        skills: [],
        education: [],
        languages: [],
        certificates: [],
        experiences: [],
        linkedIn: "",
        resume: "",
        resume_path: "",
        score: 0,
        date: new Date().toISOString(),
        detailScore: {},
        extracted_informations: {},
        dataType: "unknown"
      };
    }

    // Determine the data type from the candidate object
    const dataType = candidate.dataType || "unknown";
    
    // Process based on the detected data type
    let processedData;
    switch (dataType) {
      case "regular":
        processedData = processRegularApplicationData(candidate);
        break;
      case "scraped":
        processedData = processScrapedData(candidate);
        break;
      case "scraped_users":
        processedData = processScrapedData(candidate);
        break;
      default:
        // Try to auto-detect the type based on available fields
        if (candidate.candidate || candidate.applied_date) {
          processedData = processRegularApplicationData(candidate);
        } else if (candidate.cv_file_path || candidate.profil) {
          processedData = processScrapedData(candidate);
        } else {
          // Fallback to regular processing
          processedData = processRegularApplicationData(candidate);
        }
    }
    
    // Preserve the dataType field in the processed data
    return {
      ...processedData,
      dataType: dataType
    };
  };

  // Update the getCandidateData function to use the appropriate processor
  const getCandidateData = (candidate) => {
    if (dataType === "regular") {
      return processRegularApplicationData(candidate);
    } else if (dataType === "scraped") {
      return processScrapedData(candidate);
    } else if (dataType === "scraped_users") {
      return processScrapedData(candidate);
    } else if (dataType === "mixed") {
      return processMixedData(candidate);
    } else {
      // Default fallback
      return processRegularApplicationData(candidate);
    }
  };


  // Add new function to handle delete confirmation
  const handleDeleteClick = (candidate) => {
    setCandidateToDelete(candidate);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (candidateToDelete && onDelete) {
      onDelete(candidateToDelete.id);
    }
    setDeleteConfirmOpen(false);
    setCandidateToDelete(null);
  };

  // Create a wrapper function for fetchMoreData that includes current search parameters
  const fetchMoreDataWithParams = useCallback(async (page, pageSize) => {
    if (fetchMoreData) {
      console.log("🔍 INFINITE SCROLL DEBUG - Fetching more data with params:", {
        page: page,
        pageSize: pageSize,
        searchParams: currentSearchParams
      });
      return await fetchMoreData(page, pageSize, currentSearchParams);
    }
    return { results: [], count: 0, hasNext: false };
  }, [fetchMoreData, currentSearchParams]);

  // Determine the data source: use infinite scroll data if available, otherwise use local state
  const candidatesToRender = enableInfiniteScroll && infiniteScrollData ? infiniteScrollData : candidates;
  const totalCountToShow = enableInfiniteScroll && infiniteScrollTotalCount !== null ? infiniteScrollTotalCount : totalCount;

  console.log("🔍 DATA SOURCE DEBUG:", {
    enableInfiniteScroll: enableInfiniteScroll,
    infiniteScrollDataLength: infiniteScrollData ? infiniteScrollData.length : 0,
    localCandidatesLength: candidates.length,
    usingInfiniteScrollData: enableInfiniteScroll && infiniteScrollData,
    candidatesToRenderLength: candidatesToRender.length,
    localTotalCount: totalCount,
    infiniteScrollTotalCount: infiniteScrollTotalCount,
    totalCountToShow: totalCountToShow
  });


  return (
    <Box
      sx={{
        zoom: scale,
        transformOrigin: "top left",
        transition: "zoom 0.3s ease-in-out",
      }}
    >
      {/* Search bar and view toggle section */}
      {!hideSearchBar && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ApplicationSearchBar
              data={candidatesToRender}
              onSearch={handleSearchResults}
              SEARCH_API_ENDPOINT={SEARCH_API_ENDPOINT}
              AUTOCOMPLETE_API_ENDPOINT={AUTOCOMPLETE_API_ENDPOINT}
              dataType={dataType}
              location={
                dataType === "regular"
                  ? "location"
                  : ""
              }
              resultCount={totalCountToShow}
              onNavigateToBlacklist={onNavigateToBlacklist}
              onNavigateToDynamicForms={onNavigateToDynamicForms}
            />
          </Box>
        </Box>
      )}

      {/* Display message if no candidates are found */}
      {candidatesToRender.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            my: 3,
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ fontSize: "1.3rem" }}
          >
            {t(
              "applicationTable.noCandidatesFound",
              "No candidates found matching your search criteria"
            )}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            {t(
              "applicationTable.tryAdjustingFilters",
              "Try adjusting your search filters or clear them to see all candidates"
            )}
          </Typography>
        </Box>
      )}
      {/* Candidate Cards */}
      {/* Regular candidates - single card per row */}
      {candidatesToRender
        .filter(candidateRaw => candidateRaw != null) // Filter out null/undefined candidates
        .map((candidateRaw, index) => {
        const candidate = getCandidateData(candidateRaw);
        console.log("Candidate22:", candidate);
        // Debug logging for first few items
        if (index < 3) {
          console.log("🔍 RENDER DEBUG - Candidate", index, ":", {
            id: candidate.id,
            name: candidate.name || candidate.full_name,
            dataType: candidate.dataType,
            totalRendered: candidatesToRender.length
          });
        }

        // Generate stable key using composite ID to ensure uniqueness across different models
        const candidateId = candidateRaw.id || candidateRaw.candidate_id || candidateRaw.user_id;
        const compositeId = candidateRaw.compositeId || `${candidateRaw.source || 'unknown'}_${candidateId}`;
        const stableKey = compositeId ? `candidate-${compositeId}` : `candidate-fallback-${index}`;

        return (
          <StyledCard key={stableKey}>
            <CardContent
              className="p-4"
              sx={{
                pt: { xs: "48px", sm: "48px", md: "64px" },
                pb: { xs: 3, sm: 4, md: 5 },
                px: { xs: 3, sm: "48px", md: "48px" },
              }}
            >
              <Grid container spacing={2} alignItems="flex-start">
                {/* Column 1: Name, Profile, Contact Info */}
                <Grid item xs={12} sm={6} md={2.5} lg={2.5}>
                  <Box className="flex flex-col gap-1 mb-1">
                    {/* Name and Image Section */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 2,
                        gap: 2,
                      }}
                    >
                      {/* Avatar/Image */}
                      <Avatar
                        src={
                          // First check if there's a direct image URL in form_data
                          candidate.image &&
                          candidate.image !== "null" &&
                          candidate.image !== null &&
                          candidate.image.startsWith("http")
                            ? candidate.image
                            : // Then check for application.image with API URL
                            candidate.image &&
                              candidate.image !== "null" &&
                              candidate.image !== null
                            ? `${apiUrl}${candidate.image}`
                            : undefined
                        }
                        alt={`${candidate.firstName} ${candidate.lastName}`}
                        sx={{
                          width: 60,
                          height: 60,
                          border: "2px solid #e0e0e0",
                          backgroundColor: "#e0e0e0",
                          color: "#666",
                          fontSize: "1.2rem",
                          fontWeight: 500,
                        }}
                      >
                        {candidate.firstName?.charAt(0)}
                        {candidate.lastName?.charAt(0)}
                      </Avatar>

                      {/* Name and Applied Date */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{
                            fontSize: "1.5rem",
                            mb: 0.25,
                            whiteSpace: "nowrap",
                            overflow: "hidden"
                          }}
                        >
                          {candidate.name && candidate.name.length > 12 ? (
                            <Tooltip
                              title={candidate.name}
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    padding: '8px 12px',
                                    borderRadius: "6px"
                                  }
                                }
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  cursor: 'pointer',
                                  fontSize: "inherit",
                                  fontWeight: "inherit",
                                  lineHeight: "inherit",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                {candidate.name.substring(0, 12)}...
                              </Box>
                            </Tooltip>
                          ) : (
                            candidate.name
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            display: "block",
                            mt: 0.25,
                          }}
                        >
                          <Box component="span" sx={{ color: "#0017E7" }}>
                            {t("applicationTable.applied", "Postulé")}:
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: "#000000", ml: 0.5 }}
                          >
                            {formatDate(candidate.date)}
                          </Box>
                        </Typography>

                        {/* Data Type Indicator for Mixed Candidates */}
                        {dataType === "mixed" && candidate.dataType && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {console.log("🔍 RENDERING DEBUG - candidate.dataType:", candidate.dataType, "for candidate:", candidate.id)}
                            <Tooltip
                              title={
                                candidate.dataType === "regular"
                                  ? t("applicationTable.regularApplicationTooltip", "A postulé directement via la plateforme")
                                  : candidate.dataType === "scraped"
                                  ? t("applicationTable.scrapedApplicationTooltip", "Importé d'une source externe et rattaché à une offre")
                                  : candidate.dataType === "scraped_users"
                                  ? t("applicationTable.scrapedUserTooltip", "Importé d'une source externe mais non rattaché à une offre")
                                  : ""
                              }
                              arrow
                                                              componentsProps={{
                                  tooltip: {
                                    sx: {
                                      fontSize: {
                                        xs: "0.8rem",
                                        sm: "0.85rem",
                                        md: "0.9rem",
                                        lg: "0.95rem"
                                      },
                                      fontWeight: 500,
                                      padding: {
                                        xs: "6px 8px",
                                        sm: "7px 10px",
                                        md: "8px 12px"
                                      },
                                      borderRadius: "6px",
                                      maxWidth: {
                                        xs: "200px",
                                        sm: "250px",
                                        md: "300px"
                                      }
                                    }
                                  }
                                }}
                            >
                              <Chip
                                label={
                                  candidate.dataType === "regular"
                                    ? t("applicationTable.regularApplication", "Candidat")
                                    : candidate.dataType === "scraped"
                                    ? t("applicationTable.scrapedApplication", "Candidat Importé – Lié")
                                    : candidate.dataType === "scraped_users"
                                    ? t("applicationTable.scrapedUser", "Candidat Importé – Non Lié")
                                    : "Unknown"
                                }
                                size="small"
                                sx={{
                                  height: "24px",
                                  fontSize: "0.8rem",
                                  backgroundColor:
                                    candidate.dataType === "regular"
                                      ? "rgba(76, 175, 80, 0.1)"
                                      : candidate.dataType === "scraped"
                                      ? "rgba(255, 152, 0, 0.1)"
                                      : candidate.dataType === "scraped_users"
                                      ? "rgba(156, 39, 176, 0.1)"
                                      : "rgba(158, 158, 158, 0.1)",
                                  color:
                                    candidate.dataType === "regular"
                                      ? "#2e7d32"
                                      : candidate.dataType === "scraped"
                                      ? "#f57c00"
                                      : candidate.dataType === "scraped_users"
                                      ? "#7b1fa2"
                                      : "#616161",
                                  border: "1px solid",
                                  borderColor:
                                    candidate.dataType === "regular"
                                      ? "rgba(76, 175, 80, 0.3)"
                                      : candidate.dataType === "scraped"
                                      ? "rgba(255, 152, 0, 0.3)"
                                      : candidate.dataType === "scraped_users"
                                      ? "rgba(156, 39, 176, 0.3)"
                                      : "rgba(158, 158, 158, 0.3)",
                                  fontWeight: 500,
                                  "& .MuiChip-label": {
                                    px: 1,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        )}

                        {/* Source Information for DataImportViewers */}
                        {showSourceInfo && candidateRaw.source && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <Chip
                              label={
                                candidateRaw.source === "data_imported"
                                  ? t("applicationTable.dataImported", "Données Importées")
                                  : candidateRaw.source === "email_scraping"
                                  ? t("applicationTable.emailScraping", "Extraction d'E-mails")
                                  : candidateRaw.source
                              }
                              size="small"
                              sx={{
                                height: "24px",
                                fontSize: "0.8rem",
                                backgroundColor:
                                  candidateRaw.source === "data_imported"
                                    ? "rgba(33, 150, 243, 0.1)"
                                    : candidateRaw.source === "email_scraping"
                                    ? "rgba(255, 87, 34, 0.1)"
                                    : "rgba(158, 158, 158, 0.1)",
                                color:
                                  candidateRaw.source === "data_imported"
                                    ? "#1976d2"
                                    : candidateRaw.source === "email_scraping"
                                    ? "#d84315"
                                    : "#616161",
                                border: "1px solid",
                                borderColor:
                                  candidateRaw.source === "data_imported"
                                    ? "rgba(33, 150, 243, 0.3)"
                                    : candidateRaw.source === "email_scraping"
                                    ? "rgba(255, 87, 34, 0.3)"
                                    : "rgba(158, 158, 158, 0.3)",
                                fontWeight: 500,
                                "& .MuiChip-label": {
                                  px: 1,
                                  fontSize: "0.8rem",
                                },
                              }}
                            />
                          </Box>
                        )}

                      </Box>
                    </Box>

                    {/* Profile and Contact Info Section */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {/* Profile */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.9rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "1.4",
                          maxWidth: "100%",
                          color: "#000000",
                          fontWeight: 500,
                        }}
                      >
                        {candidate.profile ||
                          t(
                            "applicationTable.noProfileAvailable",
                            "No profile available"
                          )}
                      </Typography>

                      {/* Contact Info with Show More logic */}
                      {(() => {
                        const contactInfo = [
                          candidate.email && {
                            type: "email",
                            icon: <EmailIcon />,
                            label: candidate.email,
                          },
                          candidate.phone && {
                            type: "phone",
                            icon: <PhoneIcon />,
                            label: candidate.phone,
                          },
                          candidate.linkedIn && {
                            type: "linkedin",
                            icon: <LinkedInIcon />,
                            label: t(
                              "applicationTable.seeLinkedInProfile",
                              "Voir le profil LinkedIn"
                            ),
                            href: candidate.linkedIn.startsWith("http")
                              ? candidate.linkedIn
                              : `https://www.linkedin.com/in/${candidate.linkedIn}`,
                            onClick: () =>
                              window.open(
                                candidate.linkedIn.startsWith("http")
                                  ? candidate.linkedIn
                                  : `https://www.linkedin.com/in/${candidate.linkedIn}`,
                                "_blank"
                              ),
                          },
                        ].filter(Boolean);
                        


                        if (contactInfo.length === 0) return null;

                        return (
                          <Box className="flex flex-wrap gap-2">
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => {
                                const mappedContactInfo = contactInfo.map((c) => {
                                  if (c.type === "linkedin") {
                                    const linkObj = {
                                      type: "link",
                                      label: c.label,
                                      href: c.href,
                                    };
                                    return linkObj;
                                  }
                                  return c.label;
                                });
                                handleShowMore("Contact Information", mappedContactInfo);
                              }}
                              size="small"
                              sx={{
                                textTransform: "none !important",
                                color: "#0017E7",
                                fontWeight: 500,
                                fontSize: "0.875rem",
                              }}
                            >
                              {t(
                                "applicationTable.contactInformation",
                                "Contact Information"
                              )}
                            </Button>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                </Grid>

                {/* Column 2: Skills, Certificates */}
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Box
                    className="flex flex-col gap-3 mb-1"
                    sx={{
                      height: "100%",
                      justifyContent: "flex-end",
                    }}
                  >
                    {/* Skills */}
                    <Box>
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold flex items-center"
                        fontWeight="bold"
                        sx={{ fontSize: "1.1rem" }}
                      >
                        <Checklist className="w-6 h-6" sx={{ mr: 1 }} />
                        {t("applicationTable.skills", "Skills")}
                      </Typography>
                      {candidate.skills.length > 0 ? (
                        <>
                          <Chip
                            label={truncateLabel(candidate.skills[0])}
                            className="mr-3"
                            sx={{
                              fontSize: "1rem",
                              backgroundColor: "rgba(0, 23, 231, 0.08)",
                              color: "#0017E7",
                              border: "1px solid rgba(0, 23, 231, 0.15)",
                              fontWeight: 500,

                              "& .MuiChip-label": {
                                fontSize: "1rem",
                                fontWeight: 500,
                              },
                            }}
                          />
                          {candidate.skills.length > 1 && (
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() =>
                                handleShowMore("Skills", candidate.skills)
                              }
                              size="small"
                              sx={{
                                textTransform: "none !important",
                                color: "#0017E7",
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#0017E7",
                                },
                              }}
                            >
                              {t(
                                "applicationTable.seeMoreWithCount",
                                "See More ({{count}})",
                                { count: candidate.skills.length - 1 }
                              )}
                            </Button>
                          )}
                        </>
                      ) : (
                        <Chip
                          label={t(
                            "applicationTable.noSkillsListed",
                            "No Skills listed"
                          )}
                          sx={{
                            fontSize: "1rem",
                            "& .MuiChip-label": { fontSize: "1rem" },
                            backgroundColor: "rgba(0, 23, 231, 0.08)",
                            color: "#0017E7",
                            border: "1px solid rgba(0, 23, 231, 0.15)",
                            fontWeight: 500,
                          }}
                        />
                      )}
                    </Box>
                    {/* Certificates or Category (for scraped users) */}
                    {((candidate.category && candidate.category.name) ||
                      (candidate.extracted_certificates &&
                        candidate.extracted_certificates.length > 0) ||
                      (candidate.certificates &&
                        candidate.certificates.length > 0)) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="h6"
                          className="mb-1 font-semibold flex items-center"
                          fontWeight="bold"
                          sx={{ fontSize: "1.1rem" }}
                        >
                          <WorkspacePremium
                            className="w-6 h-6"
                            sx={{ mr: 1 }}
                          />
                          {candidate.category && candidate.category.name 
                            ? t("applicationTable.category", "Category")
                            : t("applicationTable.certificates", "Certificates")}
                        </Typography>
                        {(() => {
                          // For scraped users, prioritize category display
                          console.log("🔍 RENDER DEBUG - candidate.category:", candidate.category);
                          console.log("🔍 RENDER DEBUG - candidate.category.name:", candidate.category?.name);
                          if (candidate.category && candidate.category.name) {
                            console.log("🔍 RENDER DEBUG - Showing category:", candidate.category.name);
                            return (
                              <Chip
                                label={candidate.category.name}
                                sx={{
                                  fontSize: "1rem",
                                  backgroundColor: "rgba(0, 23, 231, 0.08)",
                                  color: "#0017E7",
                                  border: "1px solid rgba(0, 23, 231, 0.15)",
                                  fontWeight: 500,
                                  "& .MuiChip-label": {
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                  },
                                }}
                              />
                            );
                          }

                          const certs =
                            candidate.extracted_certificates &&
                            candidate.extracted_certificates.length > 0
                              ? candidate.extracted_certificates
                              : candidate.certificates;

                          if (!certs || certs.length === 0) {
                            return (
                              <Chip
                                label={t(
                                  "applicationTable.noCertificatesListed",
                                  "No certificates listed"
                                )}
                                sx={{
                                  fontSize: "1rem",
                                  "& .MuiChip-label": { fontSize: "1rem" },
                                  backgroundColor: "#f5f5f5",
                                  color: "#666",
                                }}
                              />
                            );
                          }

                          // Process certificates to combine text and URLs
                          const processedCerts = Array.isArray(certs)
                            ? certs.map((cert) => {
                                if (typeof cert === "string") {
                                  // If the certificate contains a URL, format it properly
                                  if (cert.includes("http")) {
                                    const [text, url] = cert.split("http");
                                    return `${text}http${url}`;
                                  }
                                  return cert;
                                }
                                return cert;
                              })
                            : [];

                          const firstCert = processedCerts[0];
                          const truncatedCert = truncateLabel(firstCert);
                          const isTruncated = firstCert !== truncatedCert;

                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Chip
                                label={truncatedCert}
                                className="mr-3"
                                sx={{
                                  fontSize: "1rem",
                                  backgroundColor: "rgba(0, 23, 231, 0.08)",
                                  color: "#0017E7",
                                  border: "1px solid rgba(0, 23, 231, 0.15)",
                                  fontWeight: 500,

                                  "& .MuiChip-label": {
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                  },
                                }}
                              />
                              {(isTruncated || processedCerts.length > 1) && (
                                <Button
                                  variant="text"
                                  color="primary"
                                  onClick={() =>
                                    handleShowMore(
                                      candidate.category && candidate.category.name 
                                        ? "Category" 
                                        : "Certificates",
                                      candidate.category && candidate.category.name 
                                        ? [candidate.category.name]
                                        : processedCerts
                                    )
                                  }
                                  size="small"
                                  sx={{
                                    textTransform: "none !important",
                                    color: "#0017E7",
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      color: "#0017E7",
                                    },
                                  }}
                                >
                                  {candidate.category && candidate.category.name
                                    ? t("applicationTable.seeMore", "See More")
                                    : processedCerts.length > 1
                                    ? t(
                                        "applicationTable.seeMoreWithCount",
                                        "See More ({{count}})",
                                        { count: processedCerts.length - 1 }
                                      )
                                    : t("applicationTable.seeMore", "See More")}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Column 3: Professional Experience, Languages */}
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Box
                    className="flex flex-col gap-3 mb-1"
                    sx={{
                      justifyContent: "flex-end",
                      height: "100%",
                      pl: { xs: 0, sm: 0, md: 0 }, // Remove left padding
                      ml: { xs: 0, sm: 0, md: -2 }, // Increased negative margin to pull content more to the left
                    }}
                  >
                    {/* Experience */}
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold flex items-center"
                        fontWeight="bold"
                        sx={{ fontSize: "1.1rem" }}
                      >
                        <WorkHistoryOutlined
                          className="w-6 h-6"
                          sx={{ mr: 1 }}
                        />
                        {t("applicationTable.experience", "Experience")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: 0.5, sm: 0.5 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {(candidate.extracted_informations
                                  ?.agexperiece || 0) >= 1 && (
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "0.875rem",
                                      color: "inherit",
                                    }}
                                  >
                                    {truncateExperienceText("+")}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body1"
                                  sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                                >
                                  {(candidate.extracted_informations
                                    ?.agexperiece || 0) < 1
                                    ? t(
                                        "applicationTable.lessThanOneYear",
                                        "Less than 1 year"
                                      )
                                    : `${
                                        candidate.extracted_informations
                                          ?.agexperiece || 0
                                      } ${t(
                                        "applicationTable.years",
                                        "years"
                                      )}`}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              height: "auto",
                              backgroundColor: "rgba(0, 23, 231, 0.08)",
                              color: "#0017E7",
                              border: "1px solid rgba(0, 23, 231, 0.15)",
                              fontWeight: 500,
                              "& .MuiChip-label": {
                                display: "flex",
                                alignItems: "center",
                                whiteSpace: "normal",
                                padding: "8px 12px",
                                fontWeight: 600,
                              },
                            }}
                          />
                          {candidate.experiences &&
                            candidate.experiences.length > 0 && (
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() =>
                                  handleShowMore(
                                    "Experience",
                                    candidate.experiences
                                  )
                                }
                                sx={{
                                  textTransform: "none !important",
                                  color: "#0017E7",
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#0017E7",
                                  },
                                }}
                                size="small"
                              >
                                {t("applicationTable.seeMore", "See More")}
                              </Button>
                            )}
                        </Box>
                      </Box>
                    </Box>
                    {/* Languages */}
                    {((candidate.extracted_languages &&
                      candidate.extracted_languages.length > 0) ||
                      (candidate.languages &&
                        candidate.languages.length > 0)) && (
                      <Box>
                        <Typography
                          variant="h6"
                          className="mb-1 font-semibold flex items-center"
                          fontWeight="bold"
                          sx={{ fontSize: "1.1rem" }}
                        >
                          <Language className="w-6 h-6" sx={{ mr: 1 }} />
                          {t("applicationTable.languages", "Languages")}
                        </Typography>
                        {(() => {
                          const langs =
                            candidate.extracted_languages &&
                            candidate.extracted_languages.length > 0
                              ? candidate.extracted_languages
                              : candidate.languages;
                          return (
                            <>
                              {langs && langs.length > 0 ? (
                                <>
                                  <Chip
                                    label={truncateLabel(langs[0])}
                                    className="mr-3"
                                    sx={{
                                      fontSize: "1rem",
                                      backgroundColor: "rgba(0, 23, 231, 0.08)",
                                      color: "#0017E7",
                                      border:
                                        "1px solid rgba(0, 23, 231, 0.15)",
                                      fontWeight: 500,
                                      "& .MuiChip-label": {
                                        fontSize: "1rem",
                                        fontWeight: 500,
                                      },
                                    }}
                                  />
                                  {langs.length > 1 && (
                                    <Button
                                      variant="text"
                                      color="primary"
                                      onClick={() =>
                                        handleShowMore("Languages", langs)
                                      }
                                      size="small"
                                      sx={{
                                        textTransform: "none !important",
                                        color: "#0017E7",
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                          color: "#0017E7",
                                        },
                                      }}
                                    >
                                      {t(
                                        "applicationTable.seeMoreWithCount",
                                        "See More ({{count}})",
                                        { count: langs.length - 1 }
                                      )}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Chip
                                  label={t(
                                    "applicationTable.noLanguagesListed",
                                    "No languages listed"
                                  )}
                                  sx={{
                                    fontSize: "1rem",
                                    "& .MuiChip-label": { fontSize: "1rem" },
                                    backgroundColor: "rgba(0, 23, 231, 0.08)",
                                    color: "#0017E7",
                                    border: "1px solid rgba(0, 23, 231, 0.15)",
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Column 4: Education and Location */}
                <Grid item xs={12} sm={6} md={2.5} lg={2.5}>
                  <Box
                    className="flex flex-col gap-3 mb-1"
                    sx={{ justifyContent: "flex-end", height: "100%" }}
                  >
                    {/* Education */}
                    <Box>
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold flex items-center"
                        fontWeight="bold"
                        sx={{ fontSize: "1.1rem" }}
                      >
                        <SchoolIcon className="w-6 h-6" sx={{ mr: 1 }} />
                        {t("applicationTable.education", "Education")}
                      </Typography>
                      {(() => {
                        // Use structured education data if available, fallback to parsed string data
                        const education = candidate.education || [];
                        if (education.length === 0) {
                          return (
                            <Chip
                              label={t(
                                "applicationTable.noEducationListed",
                                "No education listed"
                              )}
                              sx={{
                                fontSize: "1rem",
                                "& .MuiChip-label": { fontSize: "1rem" },
                                backgroundColor: "rgba(0, 23, 231, 0.08)",
                                color: "#0017E7",
                                border: "1px solid rgba(0, 23, 231, 0.15)",
                                fontWeight: 500,
                              }}
                            />
                          );
                        }
                        
                        // Handle both structured objects and strings
                        const firstEducation = education[0];
                        const educationText = typeof firstEducation === 'object'
                          ? (firstEducation.title || firstEducation.text || "Unknown") // Fix: Extract title properly
                          : String(firstEducation);
                          
                        const truncatedEducation = truncateLabel(educationText);
                        const isTruncated = educationText !== truncatedEducation;

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={truncatedEducation}
                              className="mr-3"
                              sx={{
                                fontSize: "1rem",
                                backgroundColor: "rgba(0, 23, 231, 0.08)",
                                color: "#0017E7",
                                border: "1px solid rgba(0, 23, 231, 0.15)",
                                fontWeight: 500,
                                "& .MuiChip-label": {
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                },
                              }}
                            />
                            {(isTruncated || education.length > 1) && (
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() =>
                                  handleShowMore("Education", education)
                                }
                                size="small"
                                sx={{
                                  textTransform: "none !important",
                                  color: "#0017E7",
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#0017E7",
                                  },
                                }}
                              >
                                {education.length > 1
                                  ? t(
                                      "applicationTable.seeMoreWithCount",
                                      "See More ({{count}})",
                                      { count: education.length - 1 }
                                    )
                                  : t("applicationTable.seeMore", "See More")}
                              </Button>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>

                    {/* Location */}
                    {dataType !== "scraped" && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="h6"
                          className="mb-1 font-semibold flex items-center"
                          fontWeight="bold"
                          sx={{ fontSize: "1.1rem" }}
                        >
                          <LocationIcon className="w-6 h-6" sx={{ mr: 1 }} />
                          {t("applicationTable.location", "Location")}
                        </Typography>
                        {candidate.location && candidate.location !== t("applicationTable.noLocationProvided", "No location provided") ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={truncateLocationLabel(candidate.location)}
                              className="mr-3"
                              sx={{
                                fontSize: "1rem",
                                backgroundColor: "rgba(0, 23, 231, 0.08)",
                                color: "#0017E7",
                                border: "1px solid rgba(0, 23, 231, 0.15)",
                                fontWeight: 500,
                                "& .MuiChip-label": {
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                },
                              }}
                            />
                            {candidate.location !== truncateLocationLabel(candidate.location) && (
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() =>
                                  handleShowMore(t("applicationTable.location", "Location"), [candidate.location])
                                }
                                size="small"
                                sx={{
                                  textTransform: "none !important",
                                  color: "#0017E7",
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#0017E7",
                                  },
                                }}
                              >
                                {t("applicationTable.seeMore", "See More")}
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Chip
                            label={t(
                              "applicationTable.noLocationProvided",
                              "No location provided"
                            )}
                            sx={{
                              fontSize: "1rem",
                              "& .MuiChip-label": { fontSize: "1rem" },
                              backgroundColor: "rgba(0, 23, 231, 0.08)",
                              color: "#0017E7",
                              border: "1px solid rgba(0, 23, 231, 0.15)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Column 5: Actions */}
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      width: "auto",
                      justifyContent: "flex-end",
                      alignItems: "stretch",
                      height: "100%",
                      mt: { xs: 2, sm: 0 }, // Add margin top on mobile
                    }}
                  >
                    <>
                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <OutlinedButton
                            startIcon={<PdfIcon />}
                            onClick={() =>
                              handleResumeClick(candidate.resume)
                            }
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              flex: 1,
                              height: "auto",
                              py: 0.5,
                            }}
                          >
                            {t("applicationTable.resume", "CV")}
                          </OutlinedButton>
                          <OutlinedButton
                            startIcon={<EmailIcon />}
                            onClick={() => handleEmailClick(candidateRaw)}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              flex: 1,
                              height: "auto",
                              py: 0.5,
                              borderColor: "#4CAF50",
                              color: "#4CAF50",
                              "&:hover": {
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                                borderColor: "#4CAF50",
                              },
                            }}
                          >
                            {t("applicationTable.contact", "CONTACT")}
                          </OutlinedButton>
                        </Box>
                        <OutlinedButton
                          startIcon={<HistoryIcon />}
                          onClick={() => handleEmailHistoryClick(candidateRaw)}
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            height: "auto",
                            py: 0.5,
                            mb: 1,
                          }}
                        >
                          {t("applicationTable.emailHistory", "Email History")}
                        </OutlinedButton>
                        {!hideCoverLetter && (
                          <OutlinedButton
                            startIcon={<BookIcon />}
                            onClick={() =>
                              handleCoverLetterClick(candidate.cover_letter)
                            }
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              height: "auto",
                              py: 0.5,
                            }}
                          >
                            {t("applicationTable.coverLetter", "LETTRE")}
                          </OutlinedButton>
                        )}
                        {onDelete && (
                          <OutlinedButton
                            startIcon={<Delete />}
                            onClick={() => {
                              setCandidateToDelete(candidateRaw);
                              setDeleteConfirmOpen(true);
                            }}
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              height: "auto",
                              py: 0.5,
                              borderColor: "#dc3545",
                              color: "#dc3545",
                              "&:hover": {
                                backgroundColor: "rgba(220, 53, 69, 0.1)",
                                borderColor: "#dc3545",
                              },
                            }}
                          >
                            {t("applicationTable.delete", "DELETE")}
                          </OutlinedButton>
                        )}

                    </>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        );
        })}


      {/* "Show More" Dialog */}
      <Dialog
        open={moreItemsDialogOpen}
        onClose={() => setMoreItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "rgba(0, 23, 231, 0.03)",
            borderBottom: "1px solid rgba(0, 23, 231, 0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 600,
              color: "#0017E7",
              fontSize: "1.2rem",
            }}
          >
            {t(
              `applicationTable.dialogTitles.${moreItemsDialogTitle
                .toLowerCase()
                .replace(/\s+/g, "")}`,
              moreItemsDialogTitle
            )}
          </Typography>
          <IconButton
            aria-label={t("applicationTable.close", "close")}
            onClick={() => setMoreItemsDialogOpen(false)}
            sx={{
              color: "rgba(0, 23, 231, 0.6)",
              "&:hover": {
                color: "#0017E7",
                backgroundColor: "rgba(0, 23, 231, 0.08)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          {moreItemsDialogTitle === "Experience" ? (
            <Box sx={{ maxHeight: "70vh", padding: { xs: 2, sm: 2.5, md: 3 } }}>
              {moreItemsDialogContent.map((experience, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    borderRadius: 2,
                    border: "1px solid rgba(0, 23, 231, 0.1)",
                    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.05)",
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 23, 231, 0.02)" : "#ffffff",
                    transition: "transform 0.2s",
                    position: "relative", // for absolute positioning of date
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0, 23, 231, 0.08)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1 }}>
                    {/* Experience Position - Main Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#0017E7",
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                        flex: 1,
                      }}
                    >
                      {experience.position || experience.text || "No position"}
                    </Typography>

                    {/* Date Range - Top Right as Chip */}
                    {experience.date_range && (
                      <Chip
                        label={experience.date_range}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(0, 23, 231, 0.08)",
                          color: "#0017E7",
                          border: "1px solid rgba(0, 23, 231, 0.15)",
                          fontWeight: 500,
                          fontSize: "0.8rem",
                          height: "24px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  {/* Company Name */}
                  {experience.company && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(0, 23, 231, 0.8)",
                        mb: 1,
                        fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                      }}
                    >
                      {experience.company}
                    </Typography>
                  )}

                  {/* Responsibilities */}
                  {experience.responsibilities && experience.responsibilities.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#333",
                          mb: 1,
                          fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                        }}
                      >
                        {t("applicationTable.responsibilities", "Responsibilities:")}
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {experience.responsibilities.map((responsibility, respIndex) => (
                          <Typography
                            key={respIndex}
                            component="li"
                            variant="body2"
                            sx={{
                              color: "#555",
                              mb: 0.5,
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              lineHeight: 1.4,
                            }}
                          >
                            {responsibility}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : moreItemsDialogTitle === "Education" ? (
            <Box sx={{ maxHeight: "70vh", padding: { xs: 2, sm: 2.5, md: 3 } }}>
              {moreItemsDialogContent.map((education, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    borderRadius: 2,
                    border: "1px solid rgba(0, 23, 231, 0.1)",
                    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.05)",
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 23, 231, 0.02)" : "#ffffff",
                    transition: "transform 0.2s",
                    position: "relative", // for absolute positioning of date
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0, 23, 231, 0.08)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1 }}>
                    {/* Education Title - Matching Experience Style */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#0017E7",
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                        flex: 1,
                      }}
                    >
                      {typeof education === 'object'
                          ? (education.title || education.text || "Unknown")
                          : String(education)}
                    </Typography>

                    {/* Date Range - Top Right as Chip */}
                    {typeof education === 'object' && education.date_range && (
                      <Chip
                        label={education.date_range}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(0, 23, 231, 0.08)",
                          color: "#0017E7",
                          border: "1px solid rgba(0, 23, 231, 0.15)",
                          fontWeight: 500,
                          fontSize: "0.8rem",
                          height: "24px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  {/* Education Subtitle/Company */}
                  {typeof education === 'object' && education.subtitle && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(0, 23, 231, 0.8)",
                        mb: 1,
                        fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                      }}
                    >
                      {education.subtitle}
                    </Typography>
                  )}

                  {/* Education Description */}
                  {typeof education === 'object' && education.description && (
                     <Box sx={{ mt: 2 }}>
                       <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                          lineHeight: 1.5,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {education.description}
                      </Typography>
                     </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.5, sm: 0.75, md: 1 }, p: { xs: 1.5, sm: 2, md: 2 } }}>
              {moreItemsDialogContent.map((item, i) => {
                console.log(`Dialog item ${i}:`, item);
                console.log(`Item type:`, typeof item);
                console.log(`Item stringified:`, JSON.stringify(item));
                console.log(`Has type property:`, item?.type);
                console.log(`Is LinkedIn URL:`, typeof item === 'string' && item.includes('linkedin.com'));
                
                // Special handling for LinkedIn URLs that appear as strings
                if (typeof item === 'string' && item.includes('linkedin.com')) {
                  console.log('Detected LinkedIn URL string, converting to clickable link');
                  return (
                    <Chip
                      key={i}
                      label="Voir le profil LinkedIn"
                      component="a"
                      href={item}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      sx={{
                        margin: { xs: "2px", sm: "3px", md: "4px" },
                        backgroundColor: "#1976d2 !important",
                        color: "#ffffff !important",
                        border: "2px solid #1976d2",
                        fontWeight: 600,
                        minHeight: { xs: "32px", sm: "36px", md: "40px" },
                        cursor: "pointer",
                        textDecoration: "none",
                        "&:hover": {
                          backgroundColor: "#1565c0 !important",
                          transform: "scale(1.05)",
                          boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                        },
                        "& .MuiChip-label": {
                          padding: { xs: "8px 12px", sm: "10px 16px", md: "12px 20px" },
                          fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                          fontWeight: 600,
                        },
                      }}
                    />
                  );
                }
                
                // Check if it's an object with type property
                if (typeof item === "object" && item !== null && item.hasOwnProperty('type')) {
                  console.log(`Item ${i} has type property:`, item.type);
                  
                  if (item.type === "link") {
                    console.log(`Rendering clickable link for item ${i}:`, item);
                    return (
                      <Chip
                        key={i}
                        label={item.label || "Link"}
                        component="a"
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        sx={{
                          margin: { xs: "2px", sm: "3px", md: "4px" },
                          backgroundColor: "#1976d2 !important",
                          color: "#ffffff !important",
                          border: "2px solid #1976d2",
                          fontWeight: 600,
                          minHeight: { xs: "32px", sm: "36px", md: "40px" },
                          cursor: "pointer",
                          textDecoration: "none",
                          "&:hover": {
                            backgroundColor: "#1565c0 !important",
                            transform: "scale(1.05)",
                            boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                          },
                          "&:active": {
                            transform: "scale(0.98)",
                          },
                          "& .MuiChip-label": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            padding: { xs: "8px 12px", sm: "10px 16px", md: "12px 20px" },
                            fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                            fontWeight: 600,
                          },
                        }}
                      />
                    );
                  }
                  
                  // Handle any other object types - convert to string safely
                  const displayText = item.position || item.text || JSON.stringify(item);
                  console.log(`Rendering regular object chip for item ${i}:`, displayText);
                  return (
                    <Chip
                      key={i}
                      label={displayText}
                      sx={{
                        margin: { xs: "2px", sm: "3px", md: "4px" },
                        backgroundColor: "rgba(0, 23, 231, 0.08)",
                        color: "#0017E7",
                        border: "1px solid rgba(0, 23, 231, 0.15)",
                        fontWeight: 500,
                        minHeight: { xs: "28px", sm: "32px", md: "36px" },
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: { xs: "6px 8px", sm: "8px 12px", md: "12px 16px" },
                          fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                          lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                        },
                      }}
                    />
                  );
                }
                
                // Handle string items
                console.log(`Rendering string item ${i}:`, item);
                return (
                  <Chip
                    key={i}
                    label={String(item)}
                    sx={{
                      margin: { xs: "2px", sm: "3px", md: "4px" },
                      backgroundColor: "rgba(0, 23, 231, 0.08)",
                      color: "#0017E7",
                      border: "1px solid rgba(0, 23, 231, 0.15)",
                      fontWeight: 500,
                      minHeight: { xs: "28px", sm: "32px", md: "36px" },
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: { xs: "6px 8px", sm: "8px 12px", md: "12px 16px" },
                        fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                        lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            borderTop: "1px solid rgba(0, 23, 231, 0.1)",
            backgroundColor: "rgba(0, 23, 231, 0.02)",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setMoreItemsDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#0017E7",
              "&:hover": {
                backgroundColor: "rgba(0, 23, 231, 0.9)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 23, 231, 0.25)",
              },
            }}
          >
            {t("applicationTable.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>
          {t("applicationTable.confirmDeletion", "Confirm Deletion")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "applicationTable.confirmDeleteUser",
              "Are you sure you want to delete this user?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t("applicationTable.cancel", "Cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (candidateToDelete && onDelete) {
                onDelete(candidateToDelete.id);
              }
              setDeleteConfirmOpen(false);
            }}
            variant="contained"
          >
            {t("applicationTable.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Always wrap modals in ThemeProvider with defaultTheme to prevent theme errors */}
      {selectedCandidate && (
        <ThemeProvider theme={defaultTheme}>
          {isTemplateModalOpen && (() => {
            const candidateInfoData = {
              email:
                selectedCandidate.email ||
                (selectedCandidate.candidate &&
                  selectedCandidate.candidate.email),
              firstName:
                selectedCandidate.firstName ||
                selectedCandidate.first_name ||
                (selectedCandidate.candidate &&
                  selectedCandidate.candidate.first_name) ||
                "",
              lastName:
                selectedCandidate.lastName ||
                selectedCandidate.last_name ||
                (selectedCandidate.candidate &&
                  selectedCandidate.candidate.last_name) ||
                "",
              company: post_title,
              position: post_title, // Also pass position for templates that might use it
            };
            console.log("🔍 Rendering SendEmailModal with candidateInfo:", candidateInfoData);
            return (
            <SendEmailModal
              open={isTemplateModalOpen}
              onClose={() => setIsTemplateModalOpen(false)}
              recruiterId={getUserIdFromAccessToken()}
              theme={defaultTheme}
              candidateInfo={candidateInfoData}
              showTemplateSelection={true}
            />
            );
          })()}

          {isEmailHistoryOpen && (
            <EmailHistory
              open={isEmailHistoryOpen}
              onClose={() => setIsEmailHistoryOpen(false)}
              candidateEmail={
                selectedCandidate.email ||
                (selectedCandidate.candidate &&
                  selectedCandidate.candidate.email)
              }
              recruiterId={getUserIdFromAccessToken()}
              theme={defaultTheme}
            />
          )}
        </ThemeProvider>
      )}
    </Box>
  );
};

export default ApplicationTable;
